// What the assistant can read, mirroring DOCUMENT_FORMATS / IMAGE_FORMATS in
// AmazonGaiaAgents utils/prompt_utils.py and the limits in
// gaia_assistant/tools/read_file_attachment.py. Keep both sides in sync — a file outside these
// can still be uploaded and attached to a Gaia object, it just can't be read in the chat.
export const READABLE_DOCUMENT_EXTENSIONS: readonly string[] = [
  "pdf",
  "doc",
  "docx",
  "xls",
  "xlsx",
  "csv",
  "html",
  "txt",
  "md",
];
export const READABLE_IMAGE_EXTENSIONS: readonly string[] = [
  "png",
  "jpeg",
  "jpg",
  "gif",
  "webp",
];
export const MAX_READABLE_DOCUMENT_BYTES = 4_500_000;
export const MAX_READABLE_IMAGE_BYTES = 3_750_000;

export type AttachmentReadability =
  | { readable: true }
  | { readable: false; reason: "too-large"; limitBytes: number }
  | { readable: false; reason: "unsupported-type"; extension: string };

export const extensionOf = (fileName: string): string => {
  const base = fileName.split(/[/\\]/).pop() ?? fileName;
  const dot = base.lastIndexOf(".");
  return dot === -1 ? "" : base.slice(dot + 1).toLowerCase();
};

// Two decimals, trailing zeros trimmed, so a limit is never rendered larger than it is
// (one decimal would show the 3,750,000-byte image limit as "3.8 MB" and over-promise).
// roundUp is for an over-limit size: rounding to nearest would render 4,500,001 bytes as the
// same "4.5 MB" as the limit, making the warning contradict itself.
export const formatFileSize = (bytes: number, roundUp = false): string => {
  const round = roundUp ? Math.ceil : Math.round;
  if (bytes >= 1_000_000)
    return `${round(bytes / 10_000) / 100} MB`.replace(".00 MB", " MB");
  if (bytes >= 1_000) return `${round(bytes / 1_000)} KB`;
  return `${bytes} bytes`;
};

export const classifyAttachment = (file: File): AttachmentReadability => {
  const extension = extensionOf(file.name);
  const isImage = READABLE_IMAGE_EXTENSIONS.includes(extension);
  const isDocument = READABLE_DOCUMENT_EXTENSIONS.includes(extension);
  if (!isImage && !isDocument) {
    return { readable: false, reason: "unsupported-type", extension };
  }
  const limitBytes = isImage
    ? MAX_READABLE_IMAGE_BYTES
    : MAX_READABLE_DOCUMENT_BYTES;
  if (file.size > limitBytes) {
    return { readable: false, reason: "too-large", limitBytes };
  }
  return { readable: true };
};
